<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        {{ isDoctorContext ? 'Add Doctor Document' : 'Add User Document' }}
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ isDoctorContext
                          ? 'Upload a verification or identity document for the selected doctor'
                          : 'Upload an identity or verification document'
                        }}
                      </p>
                      <v-chip
                        v-if="isDoctorContext && doctorName"
                        color="success"
                        outlined
                        x-small
                        class="mt-2"
                      >
                        <v-icon left x-small>mdi-doctor</v-icon>
                        For: {{ doctorName }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-file-plus</v-icon>
                      New Document Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Document Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-file-document</v-icon>
                    Document Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="usersList"
                        item-value="id"
                        item-text="display_name"
                        label="User"
                        placeholder="Select user"
                        outlined
                        :rules="[(v) => !!v || 'User is required']"
                        :error-messages="formErrors.user_id"
                        hide-details="auto"
                        required
                        :disabled="isDoctorContext"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.doc_type_id"
                        :items="docTypes"
                        item-value="id"
                        item-text="description"
                        label="Document Type"
                        placeholder="Select document type"
                        outlined
                        :rules="[(v) => !!v || 'Document type is required']"
                        :error-messages="formErrors.doc_type_id"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-document-outline</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- File Upload Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-upload</v-icon>
                    File Upload
                  </h3>

                  <v-row>
                    <v-col cols="12">
                      <v-file-input
                        v-model="documentFile"
                        label="Document File"
                        placeholder="Upload document (PDF, JPG, PNG, DOC, DOCX)"
                        outlined
                        accept="application/pdf,image/jpeg,image/png,image/jpg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        :rules="fileRules"
                        :error-messages="formErrors.file"
                        hide-details="auto"
                        prepend-icon=""
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-upload</v-icon>
                        </template>
                      </v-file-input>
                      <div class="text-caption grey--text mt-1">
                        Supported formats: PDF, JPG, PNG, DOC, DOCX. Maximum size: 10MB
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Form Actions -->
                <div class="form-actions mt-4">
                  <v-row>
                    <v-col cols="12">
                      <v-divider class="mb-4"></v-divider>
                      <div class="d-flex justify-end align-center">
                        <v-btn
                          color="grey"
                          outlined
                          @click="resetForm"
                          class="mr-3"
                          :disabled="loading"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Reset Form
                        </v-btn>
                        <v-btn
                          color="grey darken-1"
                          outlined
                          @click="$router.back()"
                          class="mr-3"
                          :disabled="loading"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          color="primary"
                          @click="saveDocument"
                          :loading="loading"
                          :disabled="!valid || loading"
                        >
                          <v-icon left>mdi-content-save</v-icon>
                          Save Document
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "UserDocumentCreate",

  data() {
    return {
      valid: false,
      loading: false,
      documentFile: null,
      usersList: [],
      docTypes: [],
      doctorName: null,

      form: {
        user_id: null,
        doc_type_id: null,
      },

      formErrors: {},

      fileRules: [
        (v) => !!v || "Document file is required",
        (v) => {
          if (!v) return true;
          if (v.size > 10 * 1024 * 1024) return "File size must be less than 10MB";
          return true;
        },
      ],
    };
  },

  computed: {
    isDoctorContext() {
      return (
        this.$route.name === "DoctorDocumentCreatePage" &&
        !!this.$route.params.doctorId
      );
    },

    doctorId() {
      return this.isDoctorContext ? this.$route.params.doctorId : null;
    },
  },

  async mounted() {
    await Promise.all([this.loadDoctor(), this.loadDocTypes()]);
  },

  methods: {
    async loadDoctor() {
      try {
        if (this.isDoctorContext) {
          const response = await axios.get(`/users/${this.doctorId}`);
          const doctor = response.data.data || response.data;
          const name = `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim();
          this.doctorName = doctor.email ? `${name} (${doctor.email})` : name;
          this.usersList = [{ id: doctor.id, display_name: this.doctorName }];
          this.form.user_id = doctor.id;
        } else {
          const response = await axios.get("/users", { params: { rowsPerPage: -1 } });
          const users = response.data.data || [];
          this.usersList = users.map((u) => ({
            id: u.id,
            display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.user_name || u.email,
          }));
        }
      } catch (error) {
        console.error("Error loading user info:", error);
        this.$toast && this.$toast.error("Failed to load user information");
      }
    },

    async loadDocTypes() {
      try {
        const response = await axios.get("/masters", {
          params: { context: "user_documents", key: "doc_types" },
        });
        this.docTypes = response.data?.doc_types || [];
      } catch (error) {
        console.error("Error loading document types:", error);
      }
    },

    async saveDocument() {
      if (!this.$refs.form.validate()) return;

      try {
        this.loading = true;
        this.formErrors = {};

        const formData = new FormData();
        formData.append("user_id", this.form.user_id);
        formData.append("doc_type_id", this.form.doc_type_id);
        if (this.documentFile) {
          formData.append("file", this.documentFile);
        }

        const response = await axios.post("/user_documents", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.success) {
          this.$store.dispatch(
            "success/setSuccess",
            response.data.message || "Document uploaded successfully"
          );
          this.$router.back();
        } else {
          this.$store.dispatch(
            "errors/setError",
            response.data.message || "Failed to upload document"
          );
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          this.formErrors = error.response.data.errors;
          this.$store.dispatch("errors/setError", "Please check the form for errors");
        } else {
          this.$store.dispatch("errors/setError", "Error uploading document");
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        user_id: this.isDoctorContext && this.usersList.length > 0
          ? this.usersList[0].id
          : null,
        doc_type_id: null,
      };
      this.documentFile = null;
      this.formErrors = {};
      this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.page-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px 12px 0 0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  margin-top: 4px;
}

.form-section {
  background: #fafbfc;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #1976d2;
}

.section-title {
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.form-actions {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}
</style>
